import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbDateParserFormatter, NgbDateStruct, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AlertService, AuthService, UIService } from 'src/app/_services';
import { ConfirmationDialogService } from 'src/app/_services/cdialog.service';
import { FileExplorerService } from 'src/app/_services/file-explorer.service';
import { PageConfig } from 'src/app/_config/config';
import { Role } from 'src/app/_models/role';
import { MediaInfo } from 'src/app/dashboard/file-explorer-page/file-explorer-page.types';
import {
  formatDurationMs,
  importActions,
  isImportActive,
  MEDIA_IMPORT_STATUS_LABELS,
  MediaImport,
  MediaImportAction,
  MediaImportQuery,
  MediaImportService,
  MediaImportSortBy,
  MediaImportStatus,
  MediaImportSummary,
  plural,
  runPool,
  toParams,
} from 'src/app/_services/media-import.service';
import { Project } from 'src/types/Project';

/** how often the list and the summary refresh while an import is running */
const REFRESH_MS = 3000;
const SEARCH_DEBOUNCE_MS = 300;
const DELETES_AT_ONCE = 3;
/** the API takes up to 20 media */
const MAX_MEDIA = 20;
const BYTES_PER_MB = 1024 * 1024;
const PAGE_SIZES = PageConfig.limitOptions.map(option => option.key);
const SORTS: MediaImportSortBy[] = ['startedAt', 'completedAt', 'fileName', 'status', 'totalFileCount', 'totalFileSize'];
/** left out of the URL */
const DEFAULTS = { sortBy: 'startedAt', sortOrder: 'desc', limit: PAGE_SIZES[0], offset: 0 };

/** The range filters: the API's bounds, and what one typed unit is in the API's (bytes, seconds) */
const RANGES = [
  { label: 'Files', min: 'minFiles', max: 'maxFiles', per: 1, step: 1, unit: '' },
  { label: 'Total Size', min: 'minSize', max: 'maxSize', per: BYTES_PER_MB, step: 0.01, unit: 'MB' },
  { label: 'Duration', min: 'minDuration', max: 'maxDuration', per: 60, step: 0.01, unit: 'minutes' },
] as const;
const DATE_RANGES = [
  { label: 'Started', from: 'startedFrom', to: 'startedTo' },
  { label: 'Completed', from: 'completedFrom', to: 'completedTo' },
] as const;
const NO_FILTERS: Partial<MediaImportQuery> = {
  status: [], mediaId: [], minFiles: undefined, maxFiles: undefined, minSize: undefined, maxSize: undefined,
  startedFrom: undefined, startedTo: undefined, completedFrom: undefined, completedTo: undefined,
  minDuration: undefined, maxDuration: undefined,
};

const ACTION_LABELS: Record<MediaImportAction, string> = {
  cancel: 'Cancel',
  dismiss: 'Dismiss',
  rollback: 'Roll back',
  delete: 'Delete',
};

/** What an action does, for its confirmation (invenire-v2's wording, and the API's for InvUI's own) */
function describeAction(action: MediaImportAction, row: MediaImport) {
  switch(action) {
    case 'cancel':
      return 'Stops the import and removes everything it added, including the uploaded JSON.';
    case 'dismiss':
      return 'The import already rolled back, so nothing was indexed. This marks it cancelled and clears its error; the uploaded JSON is kept.';
    case 'rollback':
      return 'Indexed data for this import (MongoDB, tree paths, Elasticsearch) will be removed in the background. The uploaded JSON file in storage will be kept.';
    case 'delete':
      return row.status === 'completed'
        ? 'Removes its files from the project and search, and deletes the uploaded JSON.'
        : 'Nothing from this import is in the project anymore. This deletes the uploaded JSON and removes the row.';
  }
}

const isDeletable = (row: MediaImport) => importActions(row.status).includes('delete');

/** 'YYYY-MM-DD' as the date picker takes it */
function toDate(day: string): NgbDateStruct {
  if(!day) { return null; }
  const [year, month, date] = day.split('-').map(Number);
  return { year, month, day: date };
}

/** A picked day as the API takes it; null for text the date picker couldn't read (it leaves it a string) */
function toDay(date: NgbDateStruct | string): string {
  if(!date) { return undefined; }
  if(typeof date === 'string') { return null; }
  return [date.year, date.month, date.day].map(n => String(n).padStart(2, '0')).join('-');
}

function firstFew(labels: string[]) {
  return labels.length > 2 ? `${labels.slice(0, 2).join(', ')} +${labels.length - 2}` : labels.join(', ');
}

/** A project's media imports, as invenire-v2's Media Imports page */
@Component({
    selector: 'app-project-imports',
    templateUrl: './project-imports.component.html',
    styleUrls: ['./project-imports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectImportsComponent implements OnInit, OnDestroy {
  @ViewChild('filtersDialog') filtersDialog: TemplateRef<unknown>;

  projectId: string;
  /** the project passed in navigation state, for the breadcrumb */
  project: Project;
  /** may upload and change imports; everyone else only reads them */
  isAdmin: boolean;
  imports: MediaImport[] = [];
  count = 0;
  summary: MediaImportSummary;
  loading = true;
  msg: string;
  /** the search, filters, sort and page, as the URL has them */
  query: MediaImportQuery;
  /** the project's media, for the media filter; undefined while loading */
  media: MediaInfo[];
  mediaError = false;
  /** the filters dialog's values: bounds in the units typed, days as the date picker has them */
  draft: Record<string, any>;
  /** the deletable rows picked for a bulk delete */
  selected = new Set<string>();
  deleting = false;

  readonly statuses = Object.entries(MEDIA_IMPORT_STATUS_LABELS) as [MediaImportStatus, string][];
  readonly statusLabel = MEDIA_IMPORT_STATUS_LABELS;
  readonly pageSizes = PAGE_SIZES;
  readonly ranges = RANGES;
  readonly dateRanges = DATE_RANGES;
  readonly importActions = importActions;
  readonly isDeletable = isDeletable;
  readonly actionLabel = ACTION_LABELS;
  readonly plural = plural;

  /** imports uploaded here, to say when each finishes */
  private started = new Set<string>();
  private search = new Subject<string>();
  private subscriptions: Subscription[] = [];
  private request: Subscription;
  private listTimer: ReturnType<typeof setTimeout>;
  private summaryTimer: ReturnType<typeof setTimeout>;
  private dialog: NgbModalRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private mediaImportService: MediaImportService,
    private fileExplorerService: FileExplorerService,
    private authService: AuthService,
    private alert: AlertService,
    private uiservice: UIService,
    private confirmationDialogService: ConfirmationDialogService,
    private modalService: NgbModal,
    private dateFormatter: NgbDateParserFormatter,
  ) { }

  ngOnInit() {
    this.uiservice.setTitle('Media Imports');
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.project = history.state?.data?.project;
    this.isAdmin = this.authService.hasAccess(Role.CustomerAdmin);

    this.fileExplorerService.getProjectMedia(this.projectId).subscribe({
      next: ({ media }) => this.media = media,
      error: () => this.mediaError = true,
    });
    this.subscriptions.push(
      this.search.pipe(debounceTime(SEARCH_DEBOUNCE_MS)).subscribe(search => this.filter({ search: search.trim() })),
      this.route.queryParamMap.subscribe(params => {
        this.query = this.parse(params);
        this.load();
      }),
    );
    this.loadSummary();
  }

  ngOnDestroy() {
    clearTimeout(this.listTimer);
    clearTimeout(this.summaryTimer);
    this.request?.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
    this.dialog?.dismiss();
  }

  /** The query in the URL, with whatever doesn't fit it left out (the API refuses it) */
  private parse(params: ParamMap): MediaImportQuery {
    const list = (key: string) => params.get(key)?.split(',').filter(Boolean) ?? [];
    const query: MediaImportQuery = {
      search: params.get('search') || undefined,
      status: list('status').filter((s): s is MediaImportStatus => Object.keys(MEDIA_IMPORT_STATUS_LABELS).includes(s)),
      mediaId: list('mediaId'),
      sortBy: SORTS.find(sort => sort === params.get('sortBy')) ?? 'startedAt',
      sortOrder: params.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      limit: PAGE_SIZES.find(size => size === Number(params.get('limit'))) ?? PAGE_SIZES[0],
      offset: /^\d+$/.test(params.get('offset')) ? Number(params.get('offset')) : 0,
    };
    for(const key of RANGES.flatMap(r => [r.min, r.max])) {
      if(/^\d+$/.test(params.get(key))) { query[key] = Number(params.get(key)); }
    }
    for(const key of DATE_RANGES.flatMap(d => [d.from, d.to])) {
      if(/^\d{4}-\d{2}-\d{2}$/.test(params.get(key))) { query[key] = params.get(key); }
    }
    return query;
  }

  /** Put the query in the URL (so a reload keeps it); the list follows the URL */
  private go(changes: Partial<MediaImportQuery>) {
    const queryParams = toParams({ ...this.query, ...changes });
    for(const [key, value] of Object.entries(DEFAULTS)) {
      if(queryParams[key] === value) { delete queryParams[key]; }
    }
    // the project rides along in the state, so going back to this entry still names it
    this.router.navigate([], { relativeTo: this.route, queryParams, state: { data: { project: this.project } } });
  }

  /** Change the search, filters or sort, from the first page */
  filter(changes: Partial<MediaImportQuery>) {
    this.go({ ...changes, offset: 0 });
  }

  load() {
    clearTimeout(this.listTimer);
    this.request?.unsubscribe();
    this.request = this.mediaImportService.list(this.projectId, this.query).subscribe({
      next: ({ imports, count }) => {
        // a page past the end (its rows went): the last one
        if(!imports.length && count && this.query.offset) {
          return this.go({ offset: (Math.ceil(count / this.query.limit) - 1) * this.query.limit });
        }
        this.imports = imports;
        this.count = count;
        this.loading = false;
        this.msg = imports.length ? undefined
          : this.query.search || this.activeFilterCount ? 'No imports match the search or filters' : 'No imports yet';
        const deletable = new Set(imports.filter(isDeletable).map(i => i.id));
        this.selected = new Set([...this.selected].filter(id => deletable.has(id)));
        if(imports.some(i => isImportActive(i.status))) {
          this.listTimer = setTimeout(() => this.load(), REFRESH_MS);
        }
      },
      error: () => {
        this.imports = [];
        this.count = 0;
        this.loading = false;
        this.msg = 'Something went wrong';
      },
    });
  }

  /** The cards' counts, polled while the project has an import in progress */
  loadSummary() {
    clearTimeout(this.summaryTimer);
    this.mediaImportService.summary(this.projectId).subscribe({
      next: summary => {
        this.summary = summary;
        if(Object.entries(summary.counts).some(([status, n]) => n > 0 && isImportActive(status as MediaImportStatus))) {
          this.summaryTimer = setTimeout(() => this.loadSummary(), REFRESH_MS);
        }
        if(this.started.size) { this.checkStarted(); }
      },
      error: () => this.summary = undefined,
    });
  }

  private reload() {
    this.load();
    this.loadSummary();
  }

  onUploaded(row: MediaImport) {
    this.started.add(row.id);
    this.reload();
  }

  /**
   * Say when an import uploaded here finished or failed
   * shortcut: looks among the newest 100 only; enough unless over 100 are uploaded in one go
   */
  private checkStarted() {
    this.mediaImportService.list(this.projectId, { sortBy: 'startedAt', sortOrder: 'desc', limit: 100, offset: 0 }).subscribe({
      next: ({ imports }) => {
        for(const row of imports.filter(i => this.started.has(i.id) && !isImportActive(i.status))) {
          this.started.delete(row.id);
          if(row.status === 'completed') { this.alert.success(`Import finished: ${row.fileName}`); }
          if(row.status === 'failed') { this.alert.error(row.errorMessage || `Import failed: ${row.fileName}`); }
        }
      },
      error: () => undefined,
    });
  }

  countOf(...statuses: MediaImportStatus[]) {
    return statuses.reduce((sum, s) => sum + (this.summary?.counts[s] ?? 0), 0);
  }

  get inProgressDetail() {
    const [processing, queued] = [this.countOf('processing'), this.countOf('queued')];
    return [processing && `${processing} processing`, queued && `${queued} queued`].filter(Boolean).join(' · ') || 'Nothing running';
  }

  /** Totals stay 0 until the import reads the file, so a 0 only means "none" once it completed (as invenire-v2) */
  isTotalKnown(total: number, status: MediaImportStatus) {
    return total > 0 || status === 'completed';
  }

  badgeClass(status: MediaImportStatus) {
    switch(status) {
      case 'completed': return 'badge-success';
      case 'failed': return 'badge-danger';
      case 'processing': case 'rolling_back': case 'deleting': return 'badge-primary';
      default: return 'badge-secondary';
    }
  }

  /** the statuses an import moves through with a progress */
  showsProgress(status: MediaImportStatus) {
    return ['processing', 'rolling_back', 'deleting'].includes(status);
  }

  startedDetail(row: MediaImport) {
    return row.status === 'queued' ? 'Waiting to start' : row.status === 'processing' ? 'Running' : formatDurationMs(row.durationMs);
  }

  /** a queued upload may not have arrived yet */
  hasJson(row: MediaImport) {
    return row.hasSourceJson && row.status !== 'queued';
  }

  /** Open a row's JSON, unless the click was on one of its links, buttons or checkboxes */
  open(event: MouseEvent, row: MediaImport) {
    if((event.target as Element).closest('a, button, input, label') || !this.hasJson(row)) { return; }
    this.router.navigate(['/dashboard/project', this.projectId, 'imports', row.id, 'json']);
  }

  onSearch(search: string) {
    this.search.next(search);
  }

  sort(sortBy: MediaImportSortBy) {
    this.filter({ sortBy, sortOrder: this.query.sortBy === sortBy && this.query.sortOrder === 'asc' ? 'desc' : 'asc' });
  }

  sortClass(sortBy: MediaImportSortBy) {
    return this.query.sortBy !== sortBy ? 'fa-sort' : `fa-sort-${this.query.sortOrder}`;
  }

  ariaSort(sortBy: MediaImportSortBy) {
    return this.query.sortBy !== sortBy ? null : this.query.sortOrder === 'asc' ? 'ascending' : 'descending';
  }

  get pages() {
    return Math.ceil(this.count / this.query.limit);
  }

  get pageNumber() {
    return Math.floor(this.query.offset / this.query.limit) + 1;
  }

  /** Go to the previous (-1) or next (1) page */
  page(step: number) {
    this.go({ offset: Math.max(0, this.query.offset + step * this.query.limit) });
  }

  goToPage(input: HTMLInputElement) {
    const page = Math.min(Math.max(1, Math.round(Number(input.value)) || 1), this.pages);
    input.value = String(page);
    this.go({ offset: (page - 1) * this.query.limit });
  }

  setPageSize(limit: string) {
    this.go({ limit: Number(limit), offset: 0 });
  }

  // --- filters ---

  get activeFilterCount() {
    return this.chips.length;
  }

  /** One chip per filter applied */
  get chips() {
    const q = this.query;
    const chips: { label: string, text: string }[] = [];
    if(q.status.length) {
      chips.push({ label: 'Status', text: firstFew(q.status.map(s => MEDIA_IMPORT_STATUS_LABELS[s])) });
    }
    const mediaIds = [].concat(q.mediaId);
    if(mediaIds.length) {
      chips.push({ label: 'Media', text: firstFew(mediaIds.map(id => this.media?.find(m => m.id === id)?.label ?? id)) });
    }
    for(const range of RANGES) {
      const [min, max] = [q[range.min], q[range.max]].map(bound => bound === undefined ? undefined : +(bound / range.per).toFixed(2));
      if(min === undefined && max === undefined) { continue; }
      const text = min !== undefined && max !== undefined ? `${min}–${max}` : min !== undefined ? `≥ ${min}` : `≤ ${max}`;
      chips.push({ label: range.label, text: range.unit ? `${text} ${range.unit}` : text });
    }
    for(const range of DATE_RANGES) {
      const [from, to] = [q[range.from], q[range.to]].map(day => day && this.dateFormatter.format(toDate(day)));
      if(!from && !to) { continue; }
      chips.push({ label: range.label, text: from && to ? `${from} – ${to}` : from ? `from ${from}` : `to ${to}` });
    }
    return chips;
  }

  /** Narrow the list to these statuses (a summary card) */
  showStatuses(status: MediaImportStatus[]) {
    this.filter({ status });
  }

  clearFilters() {
    this.filter(NO_FILTERS);
  }

  openFilters() {
    this.draft = this.draftOf(this.query);
    // not scrollable: its body would clip the date pickers' popups
    this.dialog = this.modalService.open(this.filtersDialog, { size: 'lg', modalDialogClass: 'import-filters-dialog' });
  }

  clearDraft() {
    this.draft = this.draftOf(NO_FILTERS);
  }

  /** The filters as the dialog shows them */
  private draftOf(q: Partial<MediaImportQuery>) {
    const draft: Record<string, any> = { status: [...q.status], mediaId: [].concat(q.mediaId) };
    for(const range of RANGES) {
      for(const key of [range.min, range.max]) {
        draft[key] = q[key] === undefined ? null : +(q[key] / range.per).toFixed(2);
      }
    }
    for(const range of DATE_RANGES) {
      for(const key of [range.from, range.to]) { draft[key] = toDate(q[key]); }
    }
    return draft;
  }

  toggleIn(list: string[], value: string) {
    const i = list.indexOf(value);
    if(i < 0) {
      list.push(value);
    } else {
      list.splice(i, 1);
    }
  }

  get draftInvalid() {
    const d = this.draft;
    return RANGES.some(({ min, max }) => d[min] < 0 || d[max] < 0 || (d[min] != null && d[max] != null && d[min] > d[max]))
      || DATE_RANGES.some(({ from, to }) => {
        const [start, end] = [toDay(d[from]), toDay(d[to])];
        return start === null || end === null || (start && end && start > end);
      })
      || d.mediaId.length > MAX_MEDIA;
  }

  applyFilters() {
    const changes: Partial<MediaImportQuery> = { status: [...this.draft.status], mediaId: [...this.draft.mediaId] };
    for(const range of RANGES) {
      for(const key of [range.min, range.max]) {
        changes[key] = this.draft[key] == null ? undefined : Math.round(this.draft[key] * range.per);
      }
    }
    for(const range of DATE_RANGES) {
      for(const key of [range.from, range.to]) { changes[key] = toDay(this.draft[key]); }
    }
    this.dialog.close();
    this.filter(changes);
  }

  // --- actions ---

  /** Run an action on a row, once confirmed */
  async act(action: MediaImportAction, row: MediaImport) {
    const label = ACTION_LABELS[action];
    const confirmed = await this.confirmationDialogService
      .confirm(`${label} import`, `${row.fileName}: ${describeAction(action, row)}`, label, 'Keep it')
      .catch(() => false);
    if(!confirmed) { return; }
    const request = action === 'delete'
      ? this.mediaImportService.delete(this.projectId, row.id)
      : this.mediaImportService.act(this.projectId, row.id, action);
    request.subscribe({
      next: ({ message }) => {
        this.alert.success(message);
        this.reload();
      },
      error: ({ error }) => this.alert.error(error?.message || 'Something went wrong!'),
    });
  }

  /** Save the export's JSON under its file name */
  downloadSource(row: MediaImport) {
    this.mediaImportService.source(this.projectId, row.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = row.fileName;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.alert.error('Could not download the JSON'),
    });
  }

  // --- bulk delete ---

  toggle(id: string) {
    if(!this.selected.delete(id)) { this.selected.add(id); }
  }

  get allSelected() {
    const deletable = this.imports.filter(isDeletable);
    return deletable.length > 0 && deletable.every(row => this.selected.has(row.id));
  }

  toggleAll() {
    this.selected = new Set(this.allSelected ? [] : this.imports.filter(isDeletable).map(row => row.id));
  }

  /** Delete the selected imports once confirmed, a few at a time, and say which could not be */
  async deleteSelected() {
    const rows = this.imports.filter(row => this.selected.has(row.id));
    const confirmed = await this.confirmationDialogService.confirm(
      'Delete imports',
      `Delete ${plural(rows.length, 'import')}? Completed ones have their files removed from the project and search, and each one's uploaded JSON is deleted.`,
      'Delete', 'Keep them',
    ).catch(() => false);
    if(!confirmed) { return; }

    this.deleting = true;
    const failures: string[] = [];
    await runPool(rows, DELETES_AT_ONCE, row => firstValueFrom(this.mediaImportService.delete(this.projectId, row.id))
      .catch(({ error }) => failures.push(`${row.fileName}: ${error?.message || 'Something went wrong!'}`)));
    this.deleting = false;
    this.selected.clear();
    if(failures.length) {
      this.alert.error(`Could not delete ${failures.length} of ${plural(rows.length, 'import')}. ${failures.join('; ')}`);
    } else {
      this.alert.success(`Deleting ${plural(rows.length, 'import')} in the background`);
    }
    this.reload();
  }
}
